import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Upload, User, MessageSquare, Shield, FileText, Trash2, Download, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LegalModal, DeleteDataModal, DataExportButton } from './LegalPages';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSettings = ({ onClose }) => {
  const { user, token, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [customStatus, setCustomStatus] = useState(user?.custom_status || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      setAvatar(response.data.url);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setMessage('Failed to upload avatar');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    try {
      await axios.put(`${API}/users/me`, {
        name,
        bio,
        custom_status: customStatus,
        avatar
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setMessage('Profile updated successfully!');
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Clear all local storage and logout
      localStorage.clear();
      logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please try again.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      style={{ pointerEvents: 'auto' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#08080C] rounded-3xl shadow-2xl p-8 max-w-lg w-full border border-white/10"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Profile Settings
          </h2>
          <button
            data-testid="close-profile-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-xl ${
            message.includes('success') 
              ? 'bg-[#00FF66]/10 border border-[#00FF66]/20 text-[#00FF66]' 
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}>
            <p className="text-sm font-medium">{message}</p>
          </div>
        )}

        <div className="space-y-6">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#00F0FF]/20 shadow-lg bg-[#0F0F16]">
                {avatar ? (
                  <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-[#00F0FF]">
                    {name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <label
                htmlFor="avatar-upload"
                className="absolute bottom-0 right-0 p-2 rounded-full bg-[#00F0FF] text-black cursor-pointer hover:scale-110 transition-transform shadow-lg shadow-[#00F0FF]/20"
              >
                <Upload className="w-4 h-4" />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
            <p className="text-sm text-gray-500">Click to upload avatar</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Name
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                data-testid="profile-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 transition-all"
                placeholder="Your name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Bio
            </label>
            <textarea
              data-testid="profile-bio-input"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 resize-none transition-all"
              placeholder="Tell us about yourself..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-300">
              Custom Status
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                data-testid="profile-status-input"
                type="text"
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] focus:ring-2 focus:ring-[#00F0FF]/20 transition-all"
                placeholder="What's on your mind?"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              data-testid="profile-cancel-button"
              onClick={onClose}
              className="flex-1 py-3 rounded-full font-semibold bg-[#0F0F16] text-gray-300 border border-white/10 hover:bg-white/5 hover:text-white transition-all"
            >
              Cancel
            </button>
            <motion.button
              data-testid="profile-save-button"
              onClick={handleSave}
              disabled={loading}
              whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(0,240,255,0.3)' }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 py-3 rounded-full font-semibold text-black bg-[#00F0FF] shadow-lg shadow-[#00F0FF]/20 disabled:opacity-50 transition-all"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </motion.button>
          </div>

          {/* Privacy & Data Section */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
              Privacy & Data
            </h3>
            
            <div className="space-y-2">
              {/* Privacy Policy */}
              <button
                onClick={() => setShowPrivacy(true)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#0F0F16] hover:bg-white/5 transition-colors group"
                data-testid="privacy-policy-btn"
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Privacy Policy</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
              </button>

              {/* Terms of Service */}
              <button
                onClick={() => setShowTerms(true)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#0F0F16] hover:bg-white/5 transition-colors group"
                data-testid="terms-of-service-btn"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#FFE800]" />
                  <span className="text-white">Terms of Service</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
              </button>

              {/* Export Data */}
              <DataExportButton userId={user?.id} token={token} />

              {/* Delete Account */}
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-[#FF2A2A]/10 hover:bg-[#FF2A2A]/20 transition-colors group border border-[#FF2A2A]/20"
                data-testid="delete-account-btn"
              >
                <div className="flex items-center gap-3">
                  <Trash2 className="w-5 h-5 text-[#FF2A2A]" />
                  <span className="text-[#FF2A2A]">Delete My Account</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#FF2A2A]/50 group-hover:text-[#FF2A2A] transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Legal Modals */}
      <LegalModal type="privacy" isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <LegalModal type="terms" isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <DeleteDataModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
        onConfirm={handleDeleteAccount}
      />
    </motion.div>
  );
};

export default ProfileSettings;